/**
 * Esquemas Zod de los formularios del dominio Administración (sedes, atendidos).
 *
 * Validan la entrada ANTES de llamar a la API (`docs/frontend_conventions.md`,
 * Validación). Los tipos de formulario se **infieren** del esquema (`z.infer`),
 * no se escriben a mano. Reflejan las reglas del backend (`application/api/
 * domains/sedes.py`/`atendidos.py`): obligatorios (sede: nombre+ciudad;
 * atendido: nombre), correo con formato válido si se rellena. La unicidad
 * (nombre de sede / atendido por sede) la valida el backend; aquí no se duplica.
 */
import { z } from "zod";

/**
 * Email opcional: vacío = sin correo; con valor, debe tener formato de email.
 * El backend no exige el correo (campo opcional), pero si se rellena conviene
 * validarlo en el cliente para fallar antes y con un mensaje claro.
 */
const optionalEmail = z
  .string()
  .trim()
  .optional()
  .default("")
  .refine(
    (value) => value === "" || z.string().email().safeParse(value).success,
    "Introduce un correo electrónico válido.",
  );

/** Texto opcional que se recorta y por defecto queda vacío. */
const optionalText = z.string().trim().optional().default("");

// --------------------------------------------------------------------------
// Sede (alta y edición comparten campos; `activa` solo aplica en edición)
// --------------------------------------------------------------------------

/**
 * Esquema de **alta de sede** (`POST /sedes`). Nombre y ciudad obligatorios;
 * dirección, teléfono y email opcionales (igual que el SSR `add_sede`).
 */
export const createSedeSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio."),
  ciudad: z.string().trim().min(1, "La ciudad es obligatoria."),
  direccion: optionalText,
  telefono: optionalText,
  email: optionalEmail,
});

/** Valores del formulario de alta de sede (inferidos del esquema). */
export type CreateSedeValues = z.infer<typeof createSedeSchema>;

/**
 * Esquema de **edición de sede** (`PATCH /sedes/<id>`). Igual que el alta más el
 * toggle `activa` (que el SSR `edit_sede` también ofrece).
 */
export const editSedeSchema = createSedeSchema.extend({
  activa: z.boolean(),
});

/** Valores del formulario de edición de sede (inferidos del esquema). */
export type EditSedeValues = z.infer<typeof editSedeSchema>;

// --------------------------------------------------------------------------
// Atendido
// --------------------------------------------------------------------------

/**
 * Esquema de **alta de atendido** (`POST /atendidos`). Solo el nombre es
 * obligatorio (igual que el SSR `add_atendido`); teléfono y email opcionales. La
 * sede no va en el formulario: la API la resuelve del header `X-Sede-Id` (la sede
 * activa), no del cuerpo.
 */
export const createAtendidoSchema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio."),
  telefono: optionalText,
  email: optionalEmail,
});

/** Valores del formulario de alta de atendido (inferidos del esquema). */
export type CreateAtendidoValues = z.infer<typeof createAtendidoSchema>;

/**
 * Esquema de **edición de atendido** (`PATCH /atendidos/<id>`). Igual que el alta
 * más el toggle `activo` (checkbox del SSR `edit_atendido`).
 */
export const editAtendidoSchema = createAtendidoSchema.extend({
  activo: z.boolean(),
});

/** Valores del formulario de edición de atendido (inferidos del esquema). */
export type EditAtendidoValues = z.infer<typeof editAtendidoSchema>;
